import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useCallback, useMemo, useState } from 'react';
import { useRefresh } from 'react-admin';
import { adminRpcClient } from '@/providers/backendClient';

interface ForceProcessingButtonProps {
  sessionNote: any;
}

export const ForceProcessingButton = ({
  sessionNote,
}: ForceProcessingButtonProps) => {
  const forceProcessAvailable = useMemo(
    () => !!sessionNote?.transcript,
    [sessionNote],
  );

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const refresh = useRefresh();

  const buttonText = useMemo(
    () =>
      sessionNote?.doneAt ? 'Force Re-Process' : 'Force Processing',
    [sessionNote?.doneAt],
  );

  const handleOpenDialog = useCallback(() => {
    setConfirmDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const onForceProcess = useCallback(async () => {
    setIsProcessing(true);
    setConfirmDialogOpen(false);

    await adminRpcClient('PUBLIC_ADMIN_therapistAsset_process', [
      'therapistSessionNotes',
      sessionNote.id,
    ]).catch(err => {
      alert('Error processing session notes: ' + err.message);
    });

    setIsProcessing(false);
    refresh();
  }, [sessionNote.id, refresh]);

  return forceProcessAvailable ? (
    <>
      <Button
        disabled={isProcessing}
        onClick={handleOpenDialog}
        startIcon={
          !isProcessing ? (
            <CheckIcon />
          ) : (
            <CircularProgress size="1rem" />
          )
        }
      >
        {!isProcessing ? buttonText : 'Processing'}
      </Button>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="processing-dialog-title"
        aria-describedby="processing-dialog-description"
      >
        <DialogTitle id="processing-dialog-title">
          {'Confirm Force Processing'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="processing-dialog-description">
            Are you sure you want to{' '}
            {sessionNote?.doneAt ? 're-process' : 'process'} this
            session note? This action cannot be undone and may
            override existing processed notes and user edits.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={onForceProcess}
            color="primary"
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : null;
};
